'use client'

import { usePathname } from 'next/navigation'
import { ArrowRight, ArrowUpRight, ChevronRight, CreditCard, FileText, Truck } from 'lucide-react'
import { formatDateLong } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'

const PAGES = [
  { key: 'delivery', href: '/delivery', label: 'Хүргэлт', icon: Truck },
  { key: 'payment', href: '/payment', label: 'Төлбөр', icon: CreditCard },
  { key: 'service', href: '/service', label: 'Үйлчилгээ', icon: FileText },
] as const

type DocPageProps = {
  vendor?: any
  pageKey: 'delivery' | 'payment' | 'service'
  titleLead: string
  titleAccent: string
  description: string
  htmlContent?: string | null
  emptyMessage: string
}

export default function DocPage({ vendor, pageKey, titleLead, titleAccent, description, htmlContent, emptyMessage }: DocPageProps) {
  const pathname = usePathname()
  const updatedAt = vendor?.updatedAt || vendor?.createdAt
  const updatedLabel = updatedAt ? formatDateLong(updatedAt) : null
  const title = `${titleLead} ${titleAccent}`

  return (
    <div>
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-10" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="hidden md:flex items-center gap-1.5 text-[12px] mb-8" style={{ color: 'rgba(255,255,255,0.55)' }}>
          <a href={navigateUrl('/', pathname)} className="hover:text-white transition-colors">Нүүр</a>
          <ChevronRight className="h-3 w-3" style={{ opacity: 0.5 }} />
          <span className="text-white font-medium">{title}</span>
        </div>
        <div className="flex items-end justify-between gap-8 flex-wrap">
          <div className="max-w-3xl">
            <h1
              className="font-semibold tracking-[-0.025em] leading-[0.95] text-white"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(32px, 6vw, 60px)' }}
            >
              {title}
            </h1>
            <p className="mt-5 text-[15px] leading-7 max-w-xl" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {description}
            </p>
          </div>
          {updatedLabel && (
            <div className="text-right">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: '#a8a8b3' }}>Сүүлд шинэчилсэн</div>
              <div className="mt-2 text-[13px] tabular-nums text-white">{updatedLabel}</div>
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-12 pb-12">
        <div className="max-w-3xl">
          {htmlContent ? (
            <div className="dark-editorial-prose" dangerouslySetInnerHTML={{ __html: htmlContent }} />
          ) : (
            <div className="rounded-2xl p-10 text-center" style={{ border: '1px dashed rgba(255,255,255,0.14)' }}>
              <FileText className="h-10 w-10 mx-auto mb-3" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <p className="text-[14px]" style={{ color: 'rgba(255,255,255,0.55)' }}>{emptyMessage}</p>
            </div>
          )}
        </div>
      </div>

      {(vendor?.email || vendor?.phone) && (
        <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-12">
          <div
            className="max-w-3xl rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
            style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <div>
              <div className="text-[13px] font-semibold text-white">Нэмэлт асуулт байна уу?</div>
              <div className="text-[12px] mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {vendor?.email && <span>{vendor.email}</span>}
                {vendor?.email && vendor?.phone && <span className="mx-1.5" style={{ opacity: 0.4 }}>·</span>}
                {vendor?.phone && <span>{vendor.phone}</span>}
              </div>
            </div>
            <a
              href={navigateUrl('/contact', pathname)}
              className="h-10 px-5 rounded-full text-[13px] font-medium inline-flex items-center justify-center gap-1.5 shrink-0"
              style={{ background: 'white', color: '#0a0a0c' }}
            >
              Холбоо барих <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      )}

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-20">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase mb-4" style={{ color: '#a8a8b3' }}>Бусад хуудас</div>
          <nav className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PAGES.map((p) => {
              const active = p.key === pageKey
              const Icon = p.icon
              return (
                <a
                  key={p.key}
                  href={navigateUrl(p.href, pathname)}
                  className="group rounded-2xl p-4 transition-colors hover:bg-white/5"
                  style={{
                    background: active ? 'white' : 'rgba(255,255,255,0.035)',
                    border: active ? '1px solid white' : '1px solid rgba(255,255,255,0.07)',
                    color: active ? '#0a0a0c' : 'white',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="h-9 w-9 rounded-lg grid place-items-center shrink-0"
                      style={{
                        background: active ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)',
                        color: active ? '#0a0a0c' : 'white',
                      }}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold leading-tight">{p.label}</div>
                    </div>
                    <ArrowUpRight
                      className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      style={{ color: active ? '#0a0a0c' : 'rgba(255,255,255,0.55)' }}
                    />
                  </div>
                </a>
              )
            })}
          </nav>
        </div>
      </div>
    </div>
  )
}
